import { zodResolver } from '@hookform/resolvers/zod'
import type { PlanMenu } from '@monorepo/shared/schemas/common'
import {
  ActionTypeSchema,
  BILLING_PERIOD,
  BillingPeriodSchema,
  ENTITY_STATUS,
  EntityStatusSchema,
  PLAN_MENU,
  PRICING_MODEL,
  PricingModelSchema,
} from '@monorepo/shared/schemas/common'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import type { Plan } from '@/features/addons/types/addon.types'
import { agentService } from '@/features/agents'
import type { Agent } from '@/features/agents/types/agent.types'
import type { Entitlement } from '@/shared/components/common/EntitlementsTable'
import { PageTitle } from '@/shared/components/common/PageTitle'
import { EntitlementsManagement } from '@/shared/components/plans/entitlements'
import { toast } from '@/shared/lib/toast'
import {
  useAddon,
  useAddonCharges,
  useAddonFeatures,
  useUpdateAddon,
} from '../../hooks/useAddons'
import { AddonDrawer } from '../AddonDrawer'
import { AddonConfigurationTabs } from './AddonConfigurationTabs'
import { AddonPreviewCard } from './AddonPreviewCard'

const fixedChargeSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  amount: z.number().min(0, 'Amount must be positive'),
  description: z.string().optional(),
})

const recurringChargeSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  amount: z.number().min(0, 'Amount must be positive'),
  billingPeriod: BillingPeriodSchema,
  description: z.string().optional(),
})

const tierSchema = z.object({
  firstUnit: z.number().min(1, 'First unit must be at least 1'),
  lastUnit: z.number().min(1, 'Last unit must be at least 1'),
  perUnitPrice: z.number().min(0, 'Per unit price must be positive'),
  flatFee: z.number().min(0, 'Flat fee must be positive'),
})

const agentTierSchema = z.object({
  minUnits: z.number().min(0, 'Min units must be at least 0'),
  maxUnits: z.number().min(0, 'Max units must be positive').nullable(),
  unitPrice: z.string().min(1, 'Unit price is required'),
})

const signalPricingSchema = z.object({
  signalId: z.string().min(1, 'Signal is required'),
  signalName: z.string().min(1, 'Signal name is required'),
  signalKind: ActionTypeSchema,
  billingPeriod: BillingPeriodSchema,
  pricingModel: PricingModelSchema,
  unitPrice: z.number().min(0, 'Unit price must be positive'),
  includedUnits: z.number().min(0).optional(),
  minimumCommitmentUnits: z.number().min(0).optional(),
  tiers: z.array(agentTierSchema).optional(),
  isEnabled: z.boolean(),
})

const agentPricingSchema = z.object({
  agentId: z.string().min(1, 'Agent is required'),
  agentName: z.string().min(1, 'Agent name is required'),
  isCollapsed: z.boolean(),
  signalPricing: z.array(signalPricingSchema),
  description: z.string().optional(),
})

const planFeatureSchema = z
  .object({
    feature_id: z.string().min(1, 'Feature is required'),
    feature_name: z.string().optional(),
    billing_period: BillingPeriodSchema,
    pricing_model: PricingModelSchema,
    unit_price: z.string().min(0, 'Unit price must be positive'),
    included_units: z.number().min(0).optional(),
    minimum_commitment_units: z.number().min(0).optional(),
    tiers: z.array(tierSchema).optional(),
    description: z.string().optional(),
  })
  .refine(
    data => {
      if (
        data.pricing_model === PRICING_MODEL.tiered ||
        data.pricing_model === PRICING_MODEL.volume
      ) {
        return data.tiers && data.tiers.length > 0
      }
      return true
    },
    {
      message: 'Tiers are required for tiered and volume pricing models',
      path: ['tiers'],
    }
  )

const featurePricingSchema = z.object({
  featureId: z.string().min(1, 'Feature is required'),
  featureName: z.string().min(1, 'Feature name is required'),
  billingPeriod: BillingPeriodSchema,
  pricingModel: PricingModelSchema,
  unitPrice: z.number().min(0, 'Unit price must be positive'),
  includedUnits: z.number().min(0).optional(),
  minimumCommitmentUnits: z.number().min(0).optional(),
  tiers: z.array(tierSchema).optional(),
  description: z.string().optional(),
})

const planConfigSchema = z.object({
  name: z.string().min(1, 'Plan name is required'),
  description: z.string().optional(),
  status: EntityStatusSchema,
  currency: z.string().min(1, 'Currency is required'),
  billingPeriod: BillingPeriodSchema,
  platformFee: z.number().min(0).optional(),
  platformFeeBillingPeriod: BillingPeriodSchema.optional(),
  fixedCharges: z.array(fixedChargeSchema),
  recurringCharges: z.array(recurringChargeSchema),
  agentPricing: z.array(agentPricingSchema),
  featurePricing: z.array(featurePricingSchema),
  planFeatures: z.array(planFeatureSchema),
})

type PlanConfigData = z.infer<typeof planConfigSchema>

interface AddonDetailProps {
  addonId: string
}

export function AddonDetail({ addonId }: AddonDetailProps) {
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false)
  const [availableAgents, setAvailableAgents] = useState<Agent[]>([])
  const [activeMenu, setActiveMenu] = useState<PlanMenu>(PLAN_MENU.pricing)
  const [entitlements, setEntitlements] = useState<Entitlement[]>([])
  const [editingEntitlement, setEditingEntitlement] =
    useState<Entitlement | null>(null)

  // Fetch addon details, charges, and features
  const { data: planData } = useAddon(addonId)
  const { data: chargesData } = useAddonCharges(addonId)
  const { data: planFeaturesData } = useAddonFeatures(addonId)
  const updatePlan = useUpdateAddon()

  const form = useForm<PlanConfigData>({
    resolver: zodResolver(planConfigSchema),
    defaultValues: {
      name: 'Basic Plan',
      description: '',
      status: ENTITY_STATUS.draft,
      currency: 'USD',
      billingPeriod: BILLING_PERIOD.monthly,
      platformFee: 0,
      platformFeeBillingPeriod: BILLING_PERIOD.monthly,
      fixedCharges: [],
      recurringCharges: [],
      agentPricing: [],
      featurePricing: [],
      planFeatures: [],
    },
  })

  // Update form when data is loaded
  // Note: 'form' is intentionally NOT in dependencies - React Hook Form best practice
  // The form object reference changes on every render, but form.reset() is stable
  React.useEffect(() => {
    if (planData && chargesData) {
      const { oneTimeCharges, recurringCharges } = chargesData

      form.reset({
        name: planData.name || 'Basic Plan',
        description: planData.description || '',
        status: planData.status || ENTITY_STATUS.draft,
        currency: planData.currency || 'USD',
        billingPeriod: planData.billing_period || BILLING_PERIOD.monthly,
        platformFee: 0, // Platform fee not in base Plan schema
        platformFeeBillingPeriod: BILLING_PERIOD.monthly,
        fixedCharges: oneTimeCharges.map(charge => ({
          name: charge.charge_name,
          amount: charge.amount,
          description: charge.description || '',
        })),
        recurringCharges: recurringCharges.map(charge => ({
          name: charge.charge_name,
          amount: charge.amount,
          billingPeriod: charge.billing_frequency || BILLING_PERIOD.monthly,
          description: charge.description || '',
        })),
        agentPricing: [],
        featurePricing: [],
        planFeatures:
          planFeaturesData?.map(feature => ({
            feature_id: feature.feature_id,
            feature_name: feature.feature?.feature_name || '',
            billing_period: BILLING_PERIOD.monthly, // Default value - not in PlanFeature schema
            pricing_model: feature.pricing_model || PRICING_MODEL.flat,
            unit_price: (feature.unit_price || 0).toString(),
            included_units: feature.included_units || 0,
            minimum_commitment_units: feature.minimum_commitment || 0,
            tiers: [],
            description: '',
          })) || [],
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planData, chargesData, planFeaturesData, form.reset])

  // Watch only specific fields needed by AddonPreviewCard to avoid unnecessary re-renders
  // Using individual watch() calls instead of watch() which creates new object on every render
  const name = form.watch('name')
  const status = form.watch('status')
  const currency = form.watch('currency')

  useEffect(() => {
    const loadAgents = async () => {
      try {
        const agentsData = await agentService.getAgents({
          status: ENTITY_STATUS.active,
        })
        setAvailableAgents(agentsData.data || [])
      } catch {
        setAvailableAgents([])
      }
    }
    loadAgents()

    // Load dummy entitlements data
    const dummyEntitlements: Entitlement[] = [
      {
        id: 'ent_1',
        type: 'feature',
        name: 'Upload',
        description: 'File upload functionality',
        value: 'infinity',
        usageReset: 'monthly',
        isHardLimit: false,
      },
      {
        id: 'ent_2',
        type: 'feature',
        name: 'Download',
        description: 'File download functionality',
        value: 10,
        usageReset: 'monthly',
        isHardLimit: true,
      },
    ]
    setEntitlements(dummyEntitlements)
  }, [])

  const handleToggleStatus = async () => {
    const currentStatus = form.watch('status')
    const newStatus =
      currentStatus === ENTITY_STATUS.draft
        ? ENTITY_STATUS.active
        : currentStatus === ENTITY_STATUS.active
          ? ENTITY_STATUS.archived
          : ENTITY_STATUS.active

    try {
      await updatePlan.mutateAsync({
        id: addonId,
        data: { status: newStatus },
      })
      form.setValue('status', newStatus)
      const statusText =
        newStatus === ENTITY_STATUS.active
          ? 'activated'
          : newStatus === ENTITY_STATUS.archived
            ? 'archived'
            : 'reactivated'
      toast.success(`Addon ${statusText} successfully`)
    } catch (_error) {
      toast.error('Failed to update addon status')
    }
  }

  // handlePlanUpdate removed - React Query automatically handles cache invalidation after mutations

  return (
    <div className='space-y-6 container mx-auto py-6'>
      {/* Header */}
      <div className='p-6'>
        <PageTitle
          title='Addon Configuration'
          subtitle='Configure pricing and billing for your addon'
        />
      </div>

      {/* Two Column Layout */}
      <div className='grid grid-cols-1 lg:grid-cols-4 gap-8 px-6'>
        {/* Left Column - Plan Preview Card */}
        <div className='lg:col-span-1'>
          <div className='sticky top-6'>
            <AddonPreviewCard
              name={name}
              planId={addonId}
              status={status}
              currency={currency}
              billing_period={planData?.billing_period}
              target_audience={planData?.target_audience}
              activeMenu={activeMenu}
              onEdit={() => setIsEditDrawerOpen(true)}
              onToggleStatus={handleToggleStatus}
              onActiveMenuChange={setActiveMenu}
            />
          </div>
        </div>

        {/* Right Side - Configuration Forms */}
        <div className='lg:col-span-3 space-y-8'>
          {activeMenu === PLAN_MENU.pricing ? (
            <AddonConfigurationTabs planId={addonId} />
          ) : (
            // Entitlements Management
            <EntitlementsManagement
              entitlements={entitlements}
              setEntitlements={setEntitlements}
              availableAgents={availableAgents}
              editingEntitlement={editingEntitlement}
              setEditingEntitlement={setEditingEntitlement}
            />
          )}
        </div>
      </div>

      {/* Addon Edit Drawer */}
      <AddonDrawer
        plan={planData as Plan}
        open={isEditDrawerOpen}
        onOpenChange={setIsEditDrawerOpen}
      />
    </div>
  )
}
